import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { DashboardEntry, GuestOnly, RequireAuth } from '@/app/guards';
import { FullScreenLoader } from '@/components/brand';
import { AppShell } from '@/components/layout/app-shell';
import { DraftProvider } from '@/lib/use-draft-guard';
import { NavigationAccessibility } from '@/app/navigation';
import { RouteError } from '@/components/ui/route-error';
import { trackPage } from '@/lib/analytics';
import { config } from '@/lib/config';
import { withDemoSearch } from '@/lib/data-source';
import { pageLoaders } from '@/lib/page-loaders';
import { recoverableImport } from '@/lib/route-module';
import { legacyDestination } from '@/lib/legacy-routes';

const Login = lazy(recoverableImport(() => import('@/pages/auth/Login')));
const Signup = lazy(recoverableImport(() => import('@/pages/auth/Signup')));
const ForgotPassword = lazy(recoverableImport(() => import('@/pages/auth/ForgotPassword')));
const ResetPassword = lazy(recoverableImport(() => import('@/pages/auth/ResetPassword')));
const VerifyEmail = lazy(recoverableImport(() => import('@/pages/auth/VerifyEmail')));
const OAuthConsent = lazy(recoverableImport(() => import('@/pages/auth/OAuthConsent')));

const Welcome = lazy(recoverableImport(pageLoaders['/welcome']));
const Overview = lazy(recoverableImport(pageLoaders['/']));
const Calls = lazy(recoverableImport(pageLoaders['/calls']));
const Contacts = lazy(recoverableImport(pageLoaders['/contacts']));
const Tasks = lazy(recoverableImport(pageLoaders['/tasks']));
const Reports = lazy(recoverableImport(pageLoaders['/reports']));
const Team = lazy(recoverableImport(pageLoaders['/team']));
const Exclusions = lazy(recoverableImport(pageLoaders['/exclusions']));
const AiAnalysis = lazy(recoverableImport(pageLoaders['/ai']));
const Webhooks = lazy(recoverableImport(pageLoaders['/webhooks']));
const ApiApps = lazy(recoverableImport(pageLoaders['/api-apps']));
const Recordings = lazy(recoverableImport(pageLoaders['/recordings']));
const Billing = lazy(recoverableImport(pageLoaders['/billing']));
const Account = lazy(recoverableImport(pageLoaders['/account']));
const Help = lazy(recoverableImport(pageLoaders['/help']));
const NotFound = lazy(recoverableImport(() => import('@/pages/NotFound')));

function Root() {
  const location = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    const destination = legacyDestination(location.pathname, location.search, location.hash);
    if (destination) navigate(destination, { replace: true });
  }, [location.pathname, location.search, location.hash, navigate]);
  useEffect(() => {
    if (!config.isDemo) return;
    const params = new URLSearchParams(location.search);
    if (config.demoSwitchEnabled && (params.has('live') || params.get('source') === 'supabase')) {
      window.location.reload();
      return;
    }
    const search = withDemoSearch(location.search);
    if (search !== location.search) navigate({ ...location, search }, { replace: true });
  }, [location, navigate]);
  useEffect(() => {
    trackPage(location.pathname);
  }, [location.pathname]);
  return (
    <DraftProvider>
      <Suspense fallback={<FullScreenLoader />}>
        <NavigationAccessibility />
        <Outlet />
      </Suspense>
    </DraftProvider>
  );
}

function LegacyRedirect() {
  const { pathname, search, hash } = useLocation();
  const destination = legacyDestination(pathname, search, hash);
  return destination ? <Navigate to={destination} replace /> : <NotFound />;
}

export const router = createBrowserRouter([
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      {
        element: <GuestOnly />,
        children: [
          { path: '/login', element: <Login /> },
          { path: '/signup', element: <Signup /> },
          { path: '/forgot-password', element: <ForgotPassword /> },
        ],
      },
      { path: '/signup/verify', element: <VerifyEmail /> },
      { path: '/reset-password', element: <ResetPassword /> },
      { path: '/oauth/consent', element: <OAuthConsent /> },
      { path: '/dashboard/oauth/consent', element: <OAuthConsent /> },
      {
        element: <RequireAuth />,
        children: [
          { path: '/welcome', element: <Welcome /> },
          // The support chat in a tab of its own: SupportChatProvider (in RequireAuth) fills the window with it here.
          { path: '/chat', element: <h1 className="sr-only">Support chat</h1> },
          {
            element: <DashboardEntry />,
            children: [
              {
                element: <AppShell />,
                errorElement: <RouteError />,
                children: [
                  { index: true, element: <Overview /> },
                  { path: 'calls', element: <Calls /> },
                  { path: 'contacts', element: <Contacts /> },
                  { path: 'tasks', element: <Tasks /> },
                  { path: 'reports', element: <Reports /> },
                  { path: 'team', element: <Team /> },
                  { path: 'exclusions', element: <Exclusions /> },
                  { path: 'ai', element: <AiAnalysis /> },
                  { path: 'webhooks', element: <Webhooks /> },
                  { path: 'api-apps', element: <ApiApps /> },
                  { path: 'recordings', element: <Recordings /> },
                  { path: 'billing', element: <Billing /> },
                  { path: 'help', element: <Help /> },
                  { path: 'account', element: <Account /> },
                  { path: 'settings', element: <Navigate to="/account" replace /> },
                ],
              },
            ],
          },
        ],
      },
      { path: '/integrations', element: <Navigate to="/webhooks" replace /> },
      { path: '/callers', element: <Navigate to="/contacts" replace /> },
      { path: '/employees', element: <Navigate to="/team" replace /> },
      { path: '/plans', element: <Navigate to="/billing" replace /> },
      { path: '/storage', element: <Navigate to="/recordings" replace /> },
      { path: '/onboarding', element: <Navigate to="/welcome" replace /> },
      { path: '/dashboard/*', element: <LegacyRedirect /> },
      { path: '/reports/*', element: <LegacyRedirect /> },
      { path: '/dashboard', element: <LegacyRedirect /> },
      { path: '/app/*', element: <LegacyRedirect /> },
      { path: '*', element: <NotFound /> },
    ],
  },
]);
